//================================================================
//  Page: Requests 
//================================================================

//  Purpose: View all requests

//================================================================

//Libraries
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';
import { SetToast } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import SideNav from '../../../Components/SideNav/SideNav';
import TabView from '../../../Components/TabView/TabView';
import StatusLabel from '../../../Components/StatusLabel/StatusLabel';

//Functions
import QueryListener from '../../../Library/QueryListener';
import ConvertDate from '../../../Library/ConvertDate';

//Images

//CSS
import '../Account.css';

export default function Requests() {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);
    const navigate = useNavigate();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to store all pending requests
    const [pendingRequests, setPendingRequests] = useState([]);

    // Used to store all approved/rejected/cancelled requests
    const [requestHistory, setRequestHistory] = useState([]);

    // Used to determine the current filter (by request type)
    const [currentFilter, setCurrentFilter] = useState('All');

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    //Get all requests
    useEffect(() => {

        if (getUser === undefined) return;

        function onLoadChange(documents) {

            // Filter Pending Requests: 'In Progress' or 'Not Started'
            const pendingRequests = documents.filter(
                (request) => request.status === 'In Progress' || request.status === 'Not Started'
            );

            // Filter Request History: Conditional filtering based on 'currentFilter'
            const requestHistory = documents.filter((request) => {
                if (currentFilter === 'All') {
                    return request.status !== 'In Progress' && request.status !== 'Not Started';
                }
                return (
                    request.status === currentFilter &&
                    (request.status !== 'In Progress' || request.status !== 'Not Started')
                );
            });

            // Update State
            setPendingRequests(pendingRequests);
            setRequestHistory(requestHistory);

        }

        function onError(error) {
            console.log(error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Unable to load requests',
            });
        }

        const unsubscribe = QueryListener('requests', [
            ['createdby.email', '==', getUser.emailaddress], 
            ['tasktype', 'in', ['Digital Request', 'Digital Commercial Request']]
        ], onLoadChange, onLoadChange, onError, ['lastmodifieddate', 'desc']);

        return () => {
            unsubscribe();
        }

        // eslint-disable-next-line
    }, [getUser, currentFilter])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='Account-Container'>

            <div className='Account-Body'>
                <PageComponent
                    header={
                        <div className='flex flex-col p-4 bg-neutral-100 w-full border-solid border-1 border-neutral-300'>
                            <h4 className='flex items-center h-full cursor-default text-[22px]'>
                                My Requests
                            </h4>
                        </div>
                    }
                    body={
                        <TabView
                            defaultView={1}
                            oneTitle={'Pending'}
                            oneTag={pendingRequests?.length}
                            oneContent={
                                <div className='px-3'>

                                    {/* ----------------------------------- */}
                                    {/*  Table Container                    */}
                                    {/* ----------------------------------- */}

                                    <div className='Table-Container flex flex-col w-full h-full overflow-hidden'>

                                        {/* ======== Header ========== */}
                                        <div className='flex flex-row justify-between py-[15px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                                            <label className='font-semibold self-center'>
                                                Total: {pendingRequests.length} of {pendingRequests.length}
                                            </label>

                                        </div>

                                        {/* ======== Table Scrollable ========== */}
                                        <div className='w-full overflow-auto h-[calc(100vh-375px)]'>

                                            <table className='w-full'>
                                                <thead>
                                                    <tr className='sticky top-0 bg-white shadow-md z-10 box-border overflow-visible'>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Request ID</th>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Name</th>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Short Description</th>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Status</th>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Created</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        // No agreements found
                                                        pendingRequests.length === 0 ?
                                                            (
                                                                <tr>
                                                                    <td colSpan={7}>
                                                                        <div className='text-center p-[2%]'>
                                                                            No requests found.
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )
                                                            :
                                                            (
                                                                // Map in each commercial request
                                                                pendingRequests.map((request, index) => (
                                                                    <tr key={index} className='border-b-[#C8C8C9] border-solid border-b'>

                                                                        {/* Request ID */}
                                                                        <td className='py-[15px] px-[20px] font-semibold cursor-pointer underline' onClick={() => navigate(`/requests/${request?.requestid}`)}>
                                                                            {request?.parentrequestid?.toUpperCase()}
                                                                        </td>

                                                                        {/* Name */}
                                                                        <td className='py-[15px] px-[20px]'>
                                                                            {request?.taskname}
                                                                        </td>

                                                                        {/* Short Description */}
                                                                        <td className='py-[15px] px-[20px]'>
                                                                            {request?.taskdescription}
                                                                        </td>

                                                                        {/* Status */}
                                                                        <td className='py-[15px] px-[20px]'>
                                                                            <StatusLabel status={request?.status} tasktype={request?.tasktype}></StatusLabel>
                                                                        </td>

                                                                        {/* Created Date */}
                                                                        <td className='py-[15px] px-[20px]'>
                                                                            {ConvertDate(request?.createddate)}
                                                                        </td>

                                                                    </tr>
                                                                ))
                                                            )
                                                    }
                                                </tbody>
                                            </table>

                                        </div>

                                    </div>

                                </div>
                            }
                            twoTitle={'History'}
                            twoContent={
                                <div className='px-3'>

                                    {/* ======== Status (Buttons) ========== */}
                                    <div className='flex flex-row items-center gap-2 mb-4'>

                                        {/* All */}
                                        <button className={currentFilter === 'All' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('All')}>
                                            All
                                        </button>

                                        {/* Approved */}
                                        <button className={currentFilter === 'Completed' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Completed')}>
                                            Approved
                                        </button>

                                        {/* Rejected */}
                                        <button className={currentFilter === 'Rejected' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Rejected')}>
                                            Rejected
                                        </button>

                                        {/* Cancelled */}
                                        <button className={currentFilter === 'Cancelled' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Cancelled')}>
                                            Cancelled
                                        </button>

                                    </div>

                                    {/* ----------------------------------- */}
                                    {/*  Table Container                    */}
                                    {/* ----------------------------------- */}

                                    <div className='Table-Container flex flex-col w-full h-full overflow-hidden'>

                                        {/* ======== Header ========== */}
                                        <div className='flex flex-row justify-between py-[15px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                                            <label className='font-semibold self-center'>
                                                Total: {requestHistory.length} of {requestHistory.length}
                                            </label>

                                        </div>

                                        {/* ======== Table Scrollable ========== */}
                                        <div className='w-full overflow-auto h-[calc(100vh-443px)]'>

                                            <table className='w-full'>
                                                <thead>
                                                    <tr className='sticky top-0 bg-white shadow-md z-10 box-border overflow-visible'>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Request ID</th>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Name</th>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Short Description</th>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Status</th>
                                                        <th className='py-[15px] px-[20px] font-medium whitespace-nowrap'>Created</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        // No agreements found
                                                        requestHistory.length === 0 ?
                                                            (
                                                                <tr>
                                                                    <td colSpan={7}>
                                                                        <div className='text-center p-[2%]'>
                                                                            No requests found.
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )
                                                            :
                                                            (
                                                                // Map in each commercial request
                                                                requestHistory.map((request, index) => (
                                                                    request?.status !== 'In Progress' &&
                                                                    <tr key={index} className='border-b-[#C8C8C9] border-solid border-b last:border-0'>

                                                                        {/* Request ID */}
                                                                        <td className='py-[15px] px-[20px] font-semibold cursor-pointer underline' onClick={() => navigate(`/requests/${request?.requestid}`)}>
                                                                            {request?.parentrequestid?.toUpperCase()}
                                                                        </td>

                                                                        {/* Name */}
                                                                        <td className='py-[15px] px-[20px]'>
                                                                            {request?.taskname}
                                                                        </td>

                                                                        {/* Short Description */}
                                                                        <td className='py-[15px] px-[20px]'>
                                                                            {request?.taskdescription}
                                                                        </td>

                                                                        {/* Status */}
                                                                        <td className='py-[15px] px-[20px]'>
                                                                            <StatusLabel status={request?.status} tasktype={request?.tasktype}></StatusLabel>
                                                                        </td>

                                                                        {/* Created Date */}
                                                                        <td className='py-[15px] px-[20px]'>
                                                                            {ConvertDate(request?.createddate)}
                                                                        </td>

                                                                    </tr>
                                                                ))
                                                            )
                                                    }
                                                </tbody>
                                            </table>

                                        </div>

                                    </div>

                                </div>
                            }
                        ></TabView>
                    }
                ></PageComponent>
            </div>

            <div className='Account-SideNav'>
                <SideNav></SideNav>
            </div>

        </div>
    )
}